import { remove, pull, uniqBy } from 'lodash';
import { animateScroll } from '../../../utils/animation/animateScrollIntoView';
import { toCssPx } from '../../../utils/cssUtil';
import requestPromisedAnimationFrame from '../../../../common/utils/lib/requestPromisedAnimationFrame';

import { getToolbarHeightReductionWithKeyboard, ScrollToElementConfig } from './scrollToElementUtils';
import virtualKeyboardHeightSingleton from '../../../utils/keyboard/virtualKeyboardHeightSingleton';
import { SheetId } from '../../structural/sheet/sheetTypes';

export type ScrollCleanupConfig = {
    scrollableParent?: HTMLElement | null | undefined;
    sheetId?: SheetId | undefined;
    keyboardWillClose?: boolean;
    scrollTop?: number;
};

type ScrollExcessState = {
    scrollableParent: HTMLElement;
    sheetId: SheetId | undefined;
    paddingBottomPx: number;
    cancelAnimation?: (preservePadding?: boolean) => void;
};

/**
 * Reads the scroll range the list will have with the given padding. Padding larger than the list stretches
 * its box, so removing padding can shrink the list's height as well as its content.
 * Padding and scroll position are restored in the same frame, so nothing is painted in between.
 */
const getMaxScrollTopWithPadding = (scrollableParent: HTMLElement, paddingBottomPx: number): number => {
    const { scrollTop } = scrollableParent;
    const { paddingBottom } = scrollableParent.style;

    scrollableParent.style.paddingBottom = toCssPx(paddingBottomPx);
    const maxScrollTop = scrollableParent.scrollHeight - scrollableParent.clientHeight;

    scrollableParent.style.paddingBottom = paddingBottom;
    scrollableParent.scrollTop = scrollTop;
    return maxScrollTop;
};

/**
 * Keeps cleanup scrolling and padding aligned with the keyboard-closed layout.
 */
const getScrollExcessCleanupTarget = (
    scrollableParent: HTMLElement,
    remainingPaddingBottomPx: number,
    keyboardWillClose: boolean,
) => {
    const isKeyboardClosing = keyboardWillClose && virtualKeyboardHeightSingleton.getCurrentHeight() > 0;
    const toolbarHeightIncrease = isKeyboardClosing ? getToolbarHeightReductionWithKeyboard() : 0;

    const maxScrollTop = getMaxScrollTopWithPadding(scrollableParent, remainingPaddingBottomPx);
    const targetScrollTop = Math.max(maxScrollTop + toolbarHeightIncrease, 0);
    const temporaryPaddingBottomPx = remainingPaddingBottomPx + toolbarHeightIncrease;

    // Hold this space until the toolbar grows back, even if cleanup finishes before the keyboard closes.
    const paddingBottom = toolbarHeightIncrease
        ? `calc(${toCssPx(temporaryPaddingBottomPx)} + var(--keyboard-toolbar-bottom-padding, 0px) - var(--mobile-toolbar-padding-bottom))`
        : toCssPx(remainingPaddingBottomPx);

    return { targetScrollTop, paddingBottom };
};

const createScrollExcessHandler = () => {
    const scrollExcessStateList: Array<ScrollExcessState> = [];
    const scrollExcessCleanupList: Array<ScrollExcessState> = [];

    /**
     * When animating to element at the bottom of the screen, there are situations where toScrollTop is
     * not accessible as it exceeds the possible scrollable area.
     *
     * In this case, we want to add in extra margin to the bottom of scrollableElement to allow for the scroll
     */
    const apply = async (scrollConfig: ScrollToElementConfig | undefined, sheetId: SheetId | undefined) => {
        const { scrollableParent, bottomObstructionHeight = 0, keyboardOpenHeightIncrease = 0 } = scrollConfig || {};

        if (!scrollableParent || bottomObstructionHeight === 0) return;

        const scrollableParentRect = scrollableParent.getBoundingClientRect();
        const bottomGapPx = window.innerHeight - scrollableParentRect.bottom;
        const keyboardOpenBottomGapPx = bottomGapPx - keyboardOpenHeightIncrease;
        const paddingBottomPx = bottomObstructionHeight - keyboardOpenBottomGapPx;

        scrollExcessCleanupList.forEach((entry) => {
            if (entry.scrollableParent === scrollableParent) entry.cancelAnimation?.(true);
        });

        // Set animateScrollToResetScrollExcess function to animate back to position that resets the scrollExcess back to 0
        // - This function will be called on finish editing an element
        scrollExcessStateList.push({
            scrollableParent,
            sheetId,
            paddingBottomPx,
        });

        await requestPromisedAnimationFrame(() => {
            scrollableParent.style.paddingBottom = toCssPx(paddingBottomPx);
        });
    };

    /**
     * This function is used to clean up scroll excess padding added before for a specific scrollableParent or sheetId.
     */
    const cleanup = ({ scrollableParent, sheetId, keyboardWillClose = false, scrollTop }: ScrollCleanupConfig) => {
        const scrollExcessToClean = remove(scrollExcessStateList, (entry) => {
            if (scrollableParent) return entry.scrollableParent === scrollableParent;
            if (sheetId) return entry.sheetId === sheetId;
        });

        // Multiple requests share one applied padding and must share one cleanup animation.
        uniqBy(scrollExcessToClean, 'scrollableParent').forEach((entry) => {
            const { scrollableParent } = entry;
            scrollExcessCleanupList.forEach((pendingEntry) => {
                if (pendingEntry.scrollableParent === scrollableParent) pendingEntry.cancelAnimation?.(true);
            });
            scrollExcessCleanupList.push(entry);

            const currentScrollLeft = scrollableParent.scrollLeft;
            const currentScrollTop = scrollableParent.scrollTop;

            // Preserve the largest padding still required by another sheet or editor.
            const remainingPaddingBottomPx = scrollExcessStateList.reduce((acc, entry) => {
                if (entry.scrollableParent !== scrollableParent) return acc;
                return Math.max(acc, entry.paddingBottomPx);
            }, 0);

            const { targetScrollTop, paddingBottom } = getScrollExcessCleanupTarget(
                scrollableParent,
                remainingPaddingBottomPx,
                keyboardWillClose,
            );

            // A requested position can scroll upwards, but not past the area left once padding is removed.
            const toScrollTop = scrollTop === undefined ? targetScrollTop : Math.min(scrollTop, targetScrollTop);

            if (scrollTop === undefined && currentScrollTop <= targetScrollTop) {
                pull(scrollExcessCleanupList, entry);

                scrollableParent.style.paddingBottom = paddingBottom;
                return;
            }

            let preservePaddingOnCancel = false;
            const cancelAnimation = animateScroll({
                scrollableParent,
                interpolationFactor: 0.2,
                fromScrollLeft: currentScrollLeft,
                fromScrollTop: currentScrollTop,

                toScrollLeft: currentScrollLeft,
                toScrollTop,

                onAnimationEnd: () => {
                    pull(scrollExcessCleanupList, entry);
                    if (!preservePaddingOnCancel) scrollableParent.style.paddingBottom = paddingBottom;
                },
            });
            entry.cancelAnimation = (preservePadding = false) => {
                preservePaddingOnCancel = preservePadding;
                cancelAnimation();
            };
        });
    };

    return {
        apply,
        cleanup,
    };
};

const scrollExcessHandler = createScrollExcessHandler();

export default scrollExcessHandler;
